import {
    UPCOMING_ACKNOWLEDGED_STATUS,
    UPCOMING_CLOSED_STATUS,
    UPCOMING_OPEN_STATUS,
    UPCOMING_PENDING_STATUS,
} from '../constants';

export const UPCOMING_TERMINAL_STATUSES = [UPCOMING_ACKNOWLEDGED_STATUS, UPCOMING_CLOSED_STATUS] as const;

export type UpcomingTerminalStatus = (typeof UPCOMING_TERMINAL_STATUSES)[number];

export const UPCOMING_TERMINAL_STATUSES_SET: ReadonlySet<UpcomingTerminalStatus> = new Set(UPCOMING_TERMINAL_STATUSES);

export const normalizeUpcomingStatus = (status: unknown): string =>
    String(status ?? '')
        .trim()
        .toUpperCase();

export const isUpcomingTerminalStatus = (status: unknown): status is UpcomingTerminalStatus =>
    UPCOMING_TERMINAL_STATUSES_SET.has(normalizeUpcomingStatus(status) as UpcomingTerminalStatus);

/**
 * Statuses a service admin can move an event to, in the order the design lists them.
 */
export const UPCOMING_SELECTABLE_STATUSES = [
    UPCOMING_OPEN_STATUS,
    UPCOMING_ACKNOWLEDGED_STATUS,
    UPCOMING_PENDING_STATUS,
    UPCOMING_CLOSED_STATUS,
] as const;

export type UpcomingSelectableStatus = (typeof UPCOMING_SELECTABLE_STATUSES)[number];

export const UPCOMING_SELECTABLE_STATUSES_SET: ReadonlySet<UpcomingSelectableStatus> = new Set(
    UPCOMING_SELECTABLE_STATUSES
);

/**
 * Whether the status of an event can still be changed.
 *
 * Only the four known statuses are editable, so a row with a missing or unrecognised
 * status renders no control rather than offering a PATCH from an invalid state.
 *
 * Deliberately not `isUpcomingTerminalStatus`: that helper also treats ACKNOWLEDGED as
 * terminal (it gates the acknowledge action), but the design keeps the status dropdown
 * available on ACKNOWLEDGED rows and only removes it once an event is CLOSED.
 */
export const isUpcomingStatusEditable = (status: unknown): boolean => {
    const normalized = normalizeUpcomingStatus(status);
    if (normalized === UPCOMING_CLOSED_STATUS) {
        return false;
    }
    return UPCOMING_SELECTABLE_STATUSES_SET.has(normalized as UpcomingSelectableStatus);
};
